/**
 * Collections API Service
 *
 * API client for collection management and personas.
 */

import { API_BASE, getAuthHeaders, handleResponse } from './apiClient';

// ============================================
// Collection CRUD
// ============================================

/**
 * Create a new collection
 *
 * @param {Object} user - Firebase user object
 * @param {Object} collectionData - Collection details
 * @returns {Promise<Object>} Created collection
 */
export const createCollection = async (user, collectionData) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections`, {
      method: 'POST',
      headers,
      body: JSON.stringify(collectionData),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

/**
 * List all collections for the current user
 *
 * @param {Object} user - Firebase user object
 * @param {Object} options - Query options
 * @returns {Promise<Array>} List of collections
 */
export const listCollections = async (user, options = {}) => {
  try {
    const headers = await getAuthHeaders(user);

    const params = new URLSearchParams();
    if (options.include_projects) params.set('include_projects', 'true');

    const response = await fetch(`${API_BASE}/collections?${params}`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error listing collections:', error);
    throw error;
  }
};

/**
 * Get a specific collection
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Collection with projects
 */
export const getCollection = async (user, collectionId, options = {}) => {
  try {
    const headers = await getAuthHeaders(user);

    const params = new URLSearchParams();
    if (options.include_projects !== false) params.set('include_projects', 'true');

    const response = await fetch(`${API_BASE}/collections/${collectionId}?${params}`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting collection:', error);
    throw error;
  }
};

/**
 * Update a collection
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated collection
 */
export const updateCollection = async (user, collectionId, updates) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/${collectionId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

/**
 * Delete a collection
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteCollection = async (user, collectionId) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/${collectionId}`, {
      method: 'DELETE',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

// ============================================
// Collection Projects
// ============================================

/**
 * Add projects to a collection
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @param {Array<number>} projectIds - Project IDs to add
 * @returns {Promise<Object>} Result with added count
 */
export const addProjectsToCollection = async (user, collectionId, projectIds) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/${collectionId}/projects`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        project_ids: projectIds,
        replace: false,
      }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error adding projects to collection:', error);
    throw error;
  }
};

/**
 * Remove projects from a collection
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @param {Array<number>} projectIds - Project IDs to remove
 * @returns {Promise<Object>} Result with removed count
 */
export const removeProjectsFromCollection = async (user, collectionId, projectIds) => {
  try {
    const headers = await getAuthHeaders(user);

    const params = new URLSearchParams();
    projectIds.forEach(id => params.append('project_ids', id));

    const response = await fetch(`${API_BASE}/collections/${collectionId}/projects?${params}`, {
      method: 'DELETE',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error removing projects from collection:', error);
    throw error;
  }
};

// ============================================
// Collection from Import Session
// ============================================

/**
 * Create a collection from an import session
 *
 * @param {Object} user - Firebase user object
 * @param {string} sessionId - Import session UUID
 * @param {Object} collectionData - Collection details (name, description, color, persona_type)
 * @returns {Promise<Object>} Created collection
 */
export const createCollectionFromSession = async (user, sessionId, collectionData) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/from-session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        ...collectionData,
      }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error creating collection from session:', error);
    throw error;
  }
};

// ============================================
// Persona Presets
// ============================================

/**
 * List available persona presets
 *
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} List of persona presets
 */
export const listPersonaPresets = async (user) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/personas/presets`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error listing persona presets:', error);
    throw error;
  }
};

// ============================================
// Collection Analysis
// ============================================

/**
 * Analyze a collection for insights, relationships, and synthesis
 *
 * @param {Object} user - Firebase user object
 * @param {number} collectionId - Collection ID
 * @param {Object} options - Analysis options
 * @param {boolean} options.include_relationships - Include relationship detection (default: true)
 * @param {boolean} options.include_synthesis - Include capability synthesis (default: true)
 * @param {string} options.persona_type - Persona to apply for analysis weights
 * @returns {Promise<Object>} Analysis results with synthesis and relationships
 */
export const analyzeCollection = async (user, collectionId, options = {}) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/collections/${collectionId}/analyze`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        include_relationships: options.include_relationships !== false,
        include_synthesis: options.include_synthesis !== false,
        persona_type: options.persona_type || null,
      }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error analyzing collection:', error);
    throw error;
  }
};

export default {
  createCollection,
  listCollections,
  getCollection,
  updateCollection,
  deleteCollection,
  addProjectsToCollection,
  removeProjectsFromCollection,
  createCollectionFromSession,
  listPersonaPresets,
  analyzeCollection,
};
